export function cloneDeep<T>(val: T): T {
    const type = typeof val

    switch (type) {
        case 'undefined':
        case 'string':
        case 'bigint':
        case 'number':
        case 'boolean':
        case 'function':
            return val
        case 'symbol':
            return cloneSymbol(val as symbol) as T
        default:
            if (null === val) {
                return val
            }

            if (undefined === val) {
                return val
            }

            if (isRegexp(val)) {
                return cloneRegExp(val)
            }

            if (Array.isArray(val)) {
                return val.map(cloneDeep) as T
            }

            if (isBuffer(val)) {
                return cloneBuffer(val) as T
            }

            if (val instanceof ArrayBuffer) {
                return cloneArrayBuffer(val)
            }

            if (val instanceof Int8Array) {
                return cloneTypedArray(val)
            }

            if (val instanceof Uint8Array) {
                return cloneTypedArray(val)
            }

            if (val instanceof Uint8ClampedArray) {
                return cloneTypedArray(val)
            }

            if (val instanceof Int16Array) {
                return cloneTypedArray(val)
            }

            if (val instanceof Uint16Array) {
                return cloneTypedArray(val)
            }

            if (val instanceof Int32Array) {
                return cloneTypedArray(val)
            }

            if (val instanceof Uint32Array) {
                return cloneTypedArray(val)
            }

            if (val instanceof Float32Array) {
                return cloneTypedArray(val)
            }

            if (val instanceof Float64Array) {
                return cloneTypedArray(val)
            }

            if (isPlainObject(val)) {
                return cloneObject(val)
            }

            return val
    }
}

function cloneObject<T extends Record<string, any>>(v: T): T {
    const object: { [key: string | number]: any } = {}

    for (const key in v) {
        object[key] = cloneDeep(v[key])
    }

    return object as T
}

const valueOf = Symbol.prototype.valueOf

function isObject(o: any): o is object {
    return Object.prototype.toString.call(o) === '[object Object]'
}

export function isPlainObject(o: unknown): o is Record<string, any> {
    if (!isObject(o)) {
        return false
    }

    // If has modified constructor
    const constructor = o.constructor

    if (constructor === undefined) {
        return true
    }

    // If has modified prototype
    const prototype = constructor.prototype

    if (!isObject(prototype)) {
        return false
    }

    // If constructor does not have an Object-specific method

    if (!prototype.hasOwnProperty('isPrototypeOf')) {
        return false
    }

    // Most likely a plain Object
    return true
}

function isBuffer(val: any): val is Buffer {
    return (
        val.constructor &&
        typeof val.constructor.isBuffer === 'function' &&
        val.constructor.isBuffer(val)
    )
}

function cloneTypedArray<
    T extends
        | Float32Array
        | Float64Array
        | Int16Array
        | Int32Array
        | Int8Array
        | Uint16Array
        | Uint32Array
        | Uint8ClampedArray
        | Uint8Array,
>(val: T): T {
    // @ts-expect-error alabala
    return new val.constructor(val.buffer, val.byteOffset, val.length)
}

function cloneRegExp<T extends RegExp>(val: T): T {
    const re = val.constructor(val.source, val.flags.trim())
    re.lastIndex = val.lastIndex
    return re
}

function cloneArrayBuffer<T extends ArrayBuffer>(val: T): T {
    const res = val.constructor(val.byteLength)
    new Uint8Array(res).set(new Uint8Array(val))
    return res
}

function cloneBuffer(val: Buffer): Buffer {
    return Buffer.from(val.toString())
}

function cloneSymbol(val: symbol): symbol {
    return Object(valueOf.call(val))
}

function isRegexp(val: any): val is RegExp {
    if (val instanceof RegExp) {
        return true
    }

    return (
        typeof val.flags === 'string' &&
        typeof val.ignoreCase === 'boolean' &&
        typeof val.multiline === 'boolean' &&
        typeof val.global === 'boolean'
    )
}

if (import.meta.vitest) {
    const { expect, describe, test } = import.meta.vitest

    describe('cloneDeep', () => {
        test('undefined', () => {
            expect(cloneDeep(undefined)).toBeUndefined()
        })
        test('null', () => {
            expect(cloneDeep(null)).toBeNull()
        })
        test('string', () => {
            expect(cloneDeep('fdfdsfdsfds')).toBe('fdfdsfdsfds')
            expect(cloneDeep('12345')).toBe('12345')
        })
        test('number', () => {
            expect(cloneDeep(12345)).toBe(12345)
        })
        test('boolean', () => {
            expect(cloneDeep(true)).toBe(true)
            expect(cloneDeep(false)).toBe(false)
        })
        test('function', () => {
            const func = () => {}
            expect(cloneDeep(func)).toBe(func)
            expect(cloneDeep(func) === func).toBe(true)
        })
        test('array', () => {
            const array = [
                undefined,
                null,
                'string',
                '12345',
                12345,
                [
                    undefined,
                    null,
                    'string',
                    '12345',
                    12345,
                    {
                        undefined: undefined,
                        null: null,
                        string: 'string',
                        numericString: '12345',
                        number: 12345,
                    },
                ],
                {
                    undefined: undefined,
                    null: null,
                    string: 'string',
                    numericString: '12345',
                    array: [
                        undefined,
                        null,
                        'string',
                        '12345',
                        12345,
                        {
                            undefined: undefined,
                            null: null,
                            string: 'string',
                            numericString: '12345',
                            number: 12345,
                        },
                    ],
                    function: () => {},
                },
                true,
            ]

            const cloned = cloneDeep(array)

            expect(cloned).toStrictEqual(array)

            expect(cloned[0] === array[0]).toBe(true)
            expect(cloned[1] === array[1]).toBe(true)
            expect(cloned[2] === array[2]).toBe(true)
            expect(cloned[3] === array[3]).toBe(true)
            expect(cloned[4] === array[4]).toBe(true)
            // Assert that object types actually get cloned
            expect(cloned[5] === array[5]).toBe(false)
            // @ts-expect-error Assert child object is cloned
            expect(cloned[5][5] === array[5][5]).toBe(false)
            // @ts-expect-error Assert child array is cloned
            expect(cloned[6]['array'] === array[6]['array']).toBe(false)
            // @ts-expect-error Assert child function is the same
            expect(cloned[6]['function'] === array[6]['function']).toBe(true)
            expect(cloned[7]).toBe(true)
            expect(cloned[7] === array[7]).toBe(true)
        })
        test('object', () => {
            const object = {
                undefined: undefined,
                null: null,
                string: 'string',
                numericString: '12345',
                number: 12345,
                array: [
                    undefined,
                    null,
                    'string',
                    '12345',
                    12345,
                    {
                        undefined: undefined,
                        null: null,
                        string: 'string',
                        numericString: '12345',
                        array: [
                            undefined,
                            null,
                            'string',
                            '12345',
                            12345,
                            {
                                undefined: undefined,
                                null: null,
                                string: 'string',
                                numericString: '12345',
                            },
                        ],
                    },
                ],
                object: {
                    undefined: undefined,
                    null: null,
                    string: 'string',
                    numericString: '12345',
                    array: [
                        undefined,
                        null,
                        'string',
                        '12345',
                        12345,
                        {
                            undefined: undefined,
                            null: null,
                            string: 'string',
                            numericString: '12345',
                        },
                    ],
                    function: () => {},
                    boolean: false,
                },
                function: () => {},
                boolean: true,
            }

            const cloned = cloneDeep(object)

            expect(cloned).toStrictEqual(object)

            expect(cloned.undefined === object.undefined).toBe(true)
            expect(cloned.null === object.null).toBe(true)
            expect(cloned.string === object.string).toBe(true)
            expect(cloned.numericString === object.numericString).toBe(true)
            expect(cloned.number === object.number).toBe(true)
            // Assert that array types actually get cloned
            expect(cloned.array === object.array).toBe(false)
            expect(cloned.function === object.function).toBe(true)
            expect(cloned.boolean).toBe(true)
            expect(cloned.boolean === object.boolean).toBe(true)
            expect(cloned.array[5] === object.array[5]).toBe(false)
            // @ts-expect-error Assert nested child array is cloned
            expect(cloned.array[5]['array'] === object.array[5]['array']).toBe(
                false
            )
            expect(cloned.object === object.object).toBe(false)
            expect(cloned.object.array === object.object.array).toBe(false)
            expect(cloned.object.function === object.object.function).toBe(true)
            expect(cloned.object.boolean).toBe(false)
            expect(cloned.object.boolean === object.object.boolean).toBe(true)
        })
    })
}
